/**
 * ⚠️ TEMPLATE TEXT — REQUIRES LEGAL REVIEW BEFORE GOING LIVE.
 *
 * The referral partner terms shown on the tokenized acceptance page. The text
 * below is a drafted starting point, NOT reviewed legal copy — have a qualified
 * lawyer review it before relying on it. This is not legal advice.
 *
 * Versioning: the accepted version is recorded on the partner (consent is
 * data). When this text changes materially, bump `version` here AND
 * `ReferralProgram.PartnerTermsVersion` on the backend — partners are then
 * asked to accept again before their code keeps attributing.
 */

export interface PartnerTermsSection {
  heading: string;
  /** One entry per paragraph. */
  body: string[];
}

export interface PartnerTermsDocument {
  title: string;
  version: string;
  /** ISO date (yyyy-mm-dd) the version took effect. */
  effectiveDate: string;
  summary: string;
  sections: PartnerTermsSection[];
}

export const PARTNER_TERMS_DOCUMENT: PartnerTermsDocument = {
  title: "Referral Partner Terms",
  version: "1.0",
  effectiveDate: "2026-08-26",
  summary:
    "You recommend TimePlanned with your personal referral link; we pay you €30 for every " +
    "referred instructor who becomes a paying customer. These terms describe how referrals " +
    "are counted, when and how we pay, and the conduct we require.",
  sections: [
    {
      heading: "1. The program",
      body: [
        "TimePlanned (operated by CQUELLE GmbH) runs a referral partner program: as a partner " +
          "you receive a personal referral code and share link. When someone signs up for " +
          "TimePlanned through your link and becomes a paying customer, you earn a one-time " +
          "referral bounty. Participation is free and does not make you an employee, agent, or " +
          "legal representative of TimePlanned.",
      ],
    },
    {
      heading: "2. How referrals are attributed",
      body: [
        "A sign-up is attributed to you when the person opens your referral link and creates a " +
          "new instructor account within 90 days. Each account can be referred at most once, and " +
          "only new accounts count — existing customers, reactivations, and your own accounts " +
          "(self-referral) never qualify.",
        "As a welcome benefit, an account referred by you receives a 90-day free trial instead " +
          "of the standard 60 days.",
      ],
    },
    {
      heading: "3. Bounty and conversion",
      body: [
        "The bounty is a flat €30 per converted referral, independent of the plan, tier, or " +
          "organization size the referred customer chooses.",
        "A referral converts — and the €30 accrues to you — when the referred account has paid " +
          "its second monthly subscription invoice, or its first annual subscription invoice. A " +
          "sign-up or free trial alone earns nothing; this rule also keeps every bounty outside " +
          "the refund window, so accrued bounties are never revoked.",
        "The bounty amount in effect at the time of attribution applies to that referral, even " +
          "if the program changes later.",
      ],
    },
    {
      heading: "4. Payout",
      body: [
        "Accrued bounties are settled monthly once your unpaid balance reaches €50. Balances " +
          "below €50 simply carry over to the next month.",
        "Payouts are made by bank transfer against a credit note (Gutschrift) we issue to you. " +
          "You confirm your tax status to us (e.g. VAT-liable or Kleinunternehmer under §19 " +
          "UStG for German partners); all taxes on payouts are your own responsibility.",
      ],
    },
    {
      heading: "5. Conduct rules",
      body: [
        "You promote TimePlanned only in ways that are legal and honest. In particular you must " +
          "not: send unsolicited advertising email, messages, or calls (in Germany: §7 UWG — " +
          "cold outreach without consent is prohibited; equivalent rules apply elsewhere); bid " +
          "on or advertise against TimePlanned brand keywords in search or social advertising; " +
          "publish your code on coupon, voucher, or deal sites; or misrepresent yourself as " +
          "TimePlanned.",
        "When you recommend TimePlanned in public content (posts, videos, articles), you " +
          "disclose that you participate in the referral program.",
        "We may withhold bounties obtained through a violation of these rules and may remove a " +
          "partner from the program for cause.",
      ],
    },
    {
      heading: "6. Data",
      body: [
        "We store your name, email address, and referral statistics (referred accounts, " +
          "conversions, payouts) to run the program. We do not share the referred customers' " +
          "personal data with you, and you receive only aggregate conversion information.",
      ],
    },
    {
      heading: "7. Changes and termination",
      body: [
        "We may change these terms or the program parameters; material changes require your " +
          "renewed acceptance before your code continues attributing new referrals. Either side " +
          "can end the participation at any time; bounties already accrued under these terms " +
          "are honored unless they resulted from a violation of the conduct rules.",
      ],
    },
  ],
};
